import { reducePlace } from "~/bin/place/helpers";
import { ApiKey, BASE_METHOD, fetchApi, FetchApiOrigin } from "~/common/API/baseApi";
import { errorMessage } from "~/common/feedback";
import { formatDate } from "~/common/functions";
import type {
  ExistingPlace,
  PlaceStat,
  PlaceStatReduced,
} from "~/interfaces/publisher";
import type { PlacePostResponse } from "~/interfaces/publisher/place";

const formatDateToStatParam = (ISODate: string) =>
  ISODate.split("T").join(" ").slice(0, 19);

export default class PlaceService {
  static getPlace = async (
    placeId?: number | null | undefined | string,
    slug?: number | null | undefined | string,
    social?: 'telegram' | 'vk' | 'max',
  ) => {
    if (!placeId && !slug) {
      createError("Не указан id или slug группы");
      errorMessage("Не указан id или slug группы");
      return null;
    }

    const params: Record<string, string | number> = {};
    if (slug) {
      params.bySlug = String(slug);
    } else {
      params['byPlace[id]'] = Number(placeId);
      params['byPlace[social]'] = social || 'telegram';
    }

    const place = (await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: '/place/get',
      params: params as any,
    })) as ExistingPlace;

    return place;
  };

  static getPlacePosts = ({
    id,
    page,
  }: {
    id: string | number;
    page: string | number;
  }) =>
    fetchApi({
      origin: FetchApiOrigin.DATA_API_ORIGIN,
      endpoint: "/group-telegram/get-posts",
      params: {
        id,
        page,
        order: "desc",
        "page-size": 10,
      },
    }) as Promise<PlacePostResponse>;

  static getPlaceStat = async (placeId: number) => {
    const date = new Date();
    date.setHours(date.getHours() + 3);
    const dateTimeTo = formatDateToStatParam(date.toISOString());
    date.setFullYear(date.getFullYear() - 1);
    const dateTimeFrom = formatDateToStatParam(date.toISOString());

    const stat = (await fetchApi({
      origin: FetchApiOrigin.DATA_API_ORIGIN,
      endpoint: "/statistic/group-statistic-report",
      params: {
        groupId: placeId,
        social: "telegram",
        dateTimeFrom,
        dateTimeTo,
      },
    })) as PlaceStat[];

    const reduced = stat.map(
      ({
        postsStatistic: { countLast24h: posts },
        subscribersStatistic: { count: subscribers },
        viewsStatistic: { countLast24h: views },
        dateTime: { date },
      }) => ({
        posts: posts ?? 0,
        subscribers: subscribers ?? 0,
        views: views ?? 0,
        date: formatDate(date),
        er: (views ?? 0) / (subscribers ?? 1),
      }),
    );

    return reduced as unknown as PlaceStatReduced[];
  };

  static getOtherPlaces = async (userId: number) => {
    const places = (await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/place/telegram/search",
      params: {
        userId,
      },
    })) as ExistingPlace[];

    return places;
  };

  static getPlaceStatistic = async (placeId: number) => {
    const stats = (await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/place/telegram/statistic-chart",
      params: {
        id: placeId,
      },
    })) as PlaceStatReduced[];

    return stats;
  };

  static getPlaces = async (filter: {
    pageNumber?: number;
    fields?: string;
    categories?: number[];
    countries?: string[];
    subscribersTo?: number;
    subscribersFrom?: number;
    ageFrom?: number;
    ageTo?: number;
    genderPercent?: number;
    subscribersFemalePercentFrom?: number;
    subscribersFemalePercentTo?: number;
    sortFields?: string[];
    sortDirections?: string[];
    priceType?: string;
    search?: string;
    priceFrom?: number;
    priceTo?: number;
    platform?: 'telegram' | 'vkontakte';
  }) => {
    const endpoint = filter.platform === 'vkontakte'
      ? '/place/vk/catalog'
      : filter.platform === 'max'
        ? '/place/max/catalog'
        : '/place/telegram/catalog';
    
    const places = (await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint,
      // @ts-ignore
      params: {
        isActive: 1,
        ...filter,
      },
    })) as ExistingPlace[];

    return places;
  };

  static getPlaceFollowers = async (params: {
    id?: number;
    userId?: number;
    placeId?: number;
    placeType?: number;
    pageNumber?: number;
    perPage?: number;
    sortFields?: string[];
    sortDirections?: string[];
  }) => {
    const followers = (await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/favorite",
      // @ts-ignore
      params,
    })) as ExistingPlace[];

    return followers;
  };

  static addPlaceToFavorite = async (body: {
    placeId: number;
    social: string;
  }) => {
    const place = (await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/favorite",
      method: BASE_METHOD.POST,
      body,
    })) as ExistingPlace;

    return place;
  };

  static removePlaceFromFavorite = async (params: {
    id: number;
    social: string;
  }) => {
    const place = (await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/favorite",
      method: BASE_METHOD.DELETE,
      params,
    })) as ExistingPlace;

    return place;
  };

  static getPlacePostViews = async (postUrl: string, interval: string) => {
    const result = (await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/place/post_views",
      method: BASE_METHOD.GET,
      params: {
        postUrl,
        interval,
      },
    })) as any;

    return result;
  };
}

